import {
  Accordion,
  Button,
  Chip,
  Divider,
  InputText,
  SkeletonLoader,
  Text,
} from '@msc-b2c/shared-components';
import { useDispatch, useSelector } from 'react-redux';
import {
  getActiveSession,
  getFormatPriceCurrencyOrder,
  managePlurals,
  replacePlaceholders,
  RootState,
  setAppliedECoupon,
  updateLoggedUserEmailInput,
  validateFormField,
  clearAppliedECoupon,
  clearPaymentCouponErrorCode,
  getKeyboardType,
  useDebounce,
  isElementVisible,
  formatPriceWithoutRounding,
} from '@msc-b2c/shared-utils';
import {
  extractEcouponIssueCodeFromError,
  getEcouponErrorMessageByCode as resolveEcouponErrorMessage,
  isHandledPaymentCouponErrorCode,
} from '../../../utils/ecouponErrorUtils';
import { navigateOrRedirectToRoute } from '../../../../navigationmenu/utils/navigationMenuUtils';
import { useNavigate } from 'react-router-dom';
import { getItemLevelCouponOutcome } from '../../../utils/appliedCouponValidation';
import { getPricesCart } from '../../../utils/getPricesCart';
import { CartItem } from '../../../../../../../interfaces/src/lib/types/components/cart/CartProps';
import { getMergedCartDisplayItems } from '../../../../utils/myAreaCartUtils';
import styles from './sidesummary.module.scss';
import { SummaryProps } from '@msc-b2c/interfaces/types';
import { type ChangeEvent, useEffect, useState } from 'react';
import {
  useForm,
  type FieldErrors,
  type UseFormClearErrors,
  type UseFormRegister,
} from 'react-hook-form';
import { LoginMode, PageType } from '@msc-b2c/interfaces/enums';
import { EmailInputSection } from './components/EmailInputSection';
import {
  CHECKOUT_EMAIL_VALIDATION_EVENT,
  CheckoutEmailValidationEvent,
} from '../../../../utils/myAreaUtils';

export function SideSummary({
  items,
  labels,
  isPricesLoading,
  pageType,
}: Readonly<{
  items: CartItem[];
  labels: Readonly<SummaryProps>;
  isPricesLoading?: boolean;
  pageType?: PageType;
}>) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { checkoutLink } = useSelector((state: RootState) => state.navigationMenuData);

  const {
    prices,
    loggedUserEmailInput,
    appliedECoupon,
    paymentCouponErrorCode,
    confirmation: confirmationResponse,
  } = useSelector((state: RootState) => state.myAreaCart);
  const { is3dsBookingSuccess } = useSelector((state: RootState) => state.payment);

  const activePrice = is3dsBookingSuccess ? confirmationResponse?.price : prices?.price;
  const {
    emailOrderConfirmationTitle,
    emailOrderConfirmationDescription,
    emailInputLabel,
    emailInputPlaceholder,
    emailInputValidationRules,
    emailInputRequiredLabel,
    accordionDefaultState,
    inputFieldLabel,
    couponInputPlaceholder,
    applyCouponCtaLabel,
    couponInputValidationRules,
    ecouponErrorMessageConfigList,
    ecouponErrorMessageConfigListResources,
    genericEcouponErrorMessage,
    combinedItemErrorsMessage,
    accordionOpenIcon,
    accordionCloseIcon,
    ecouponAccordionDefaultState,
    ecouponAccordionOpenIcon,
    ecouponAccordionCloseIcon,
    ecouponVisible,
  } = labels;

  const {
    register,
    trigger,
    formState: { errors },
    clearErrors,
    setError,
    handleSubmit,
    getValues,
    setValue,
    watch,
  } = useForm<{ emailOrderConfirmation: string; eCouponCode: string }>({ shouldFocusError: false });

  const [isCouponAccordionOpen, setIsCouponAccordionOpen] = useState(
    (ecouponAccordionDefaultState ?? accordionDefaultState) === 'opened'
  );

  const eCouponValue = watch('eCouponCode');
  const couponValidationResult = validateFormField(
    eCouponValue?.trim() ?? '',
    couponInputValidationRules || []
  );
  const isApplyEnabled = couponValidationResult === true && !appliedECoupon;
  const isCouponApplied = Boolean(appliedECoupon);

  const { loginMode, bookingNumber = '', cruiseId } = getActiveSession();
  const isLightLoginMode = loginMode === LoginMode.LIGHT;
  const isCheckoutPage = pageType === PageType.CHECKOUT_PAGE;

  const showEmailSection =
    isLightLoginMode &&
    (!!emailOrderConfirmationTitle ||
      !!emailOrderConfirmationDescription ||
      !!emailInputLabel ||
      !!emailInputPlaceholder);
  const isEmailFieldAvailable = isLightLoginMode && (!!emailInputLabel || !!emailInputPlaceholder);

  useEffect(() => {
    if (loggedUserEmailInput && !getValues('emailOrderConfirmation')) {
      setValue('emailOrderConfirmation', loggedUserEmailInput);
    }
  }, [getValues, loggedUserEmailInput, setValue]);

  useEffect(() => {
    if (!isCheckoutPage || !isEmailFieldAvailable) {
      return;
    }

    const handleCheckoutEmailValidation = (event: Event) => {
      const validationEvent = event as CheckoutEmailValidationEvent;

      if (!isElementVisible('.cmp-sidesummary__email-section')) {
        validationEvent.detail?.respond(true);
        return;
      }

      trigger('emailOrderConfirmation').then((isValid) => {
        if (isValid) {
          dispatch(updateLoggedUserEmailInput(getValues('emailOrderConfirmation')));
        } else {
          dispatch(updateLoggedUserEmailInput(''));
        }

        validationEvent.detail?.respond(isValid);
      });
    };

    window.addEventListener(CHECKOUT_EMAIL_VALIDATION_EVENT, handleCheckoutEmailValidation);

    return () => {
      window.removeEventListener(CHECKOUT_EMAIL_VALIDATION_EVENT, handleCheckoutEmailValidation);
    };
  }, [getValues, isCheckoutPage, isEmailFieldAvailable, trigger]);

  useEffect(() => {
    if (appliedECoupon) {
      setValue('eCouponCode', appliedECoupon);
    } else if (getValues('eCouponCode') && !paymentCouponErrorCode) {
      setValue('eCouponCode', '');
      clearErrors('eCouponCode');
    }
  }, [appliedECoupon, paymentCouponErrorCode, clearErrors, getValues, setValue]);

  // Combined adult + minor ALLINC packages count as a single item in the summary.
  const displayItemsCount = getMergedCartDisplayItems(items).length;

  const subtotalPriceAmount = activePrice?.total.beforeDiscount; // keep the original price without turning it to Number as we need the last decimal

  const discountAmount = activePrice?.total.discountAmount;
  const couponAmount = Number(activePrice?.total.couponAmount ?? 0);

  const taxesAmount = Number(activePrice?.taxes.afterDiscount ?? 0);

  const hasSavedMoneyAmount = Number(discountAmount ?? 0) > 0;

  const formatCurrency = (value: string | number | undefined): string =>
    getFormatPriceCurrencyOrder(formatPriceWithoutRounding(String(value) || '0'));

  const hasECoupon = Boolean(ecouponVisible && labels.ecouponText && items.length > 0);
  const getEcouponErrorMessageByCode = (apiCode?: string): string =>
    resolveEcouponErrorMessage(
      apiCode,
      ecouponErrorMessageConfigList,
      ecouponErrorMessageConfigListResources,
      genericEcouponErrorMessage
    );

  useEffect(() => {
    if (!paymentCouponErrorCode) return;

    const issueCode = paymentCouponErrorCode;
    dispatch(clearPaymentCouponErrorCode());

    if (
      !isHandledPaymentCouponErrorCode(
        issueCode,
        ecouponErrorMessageConfigList,
        ecouponErrorMessageConfigListResources
      )
    ) {
      return;
    }

    dispatch(clearAppliedECoupon());
    setValue('eCouponCode', '');
    setError('eCouponCode', {
      type: 'manual',
      message: getEcouponErrorMessageByCode(issueCode),
    });
    setIsCouponAccordionOpen(true);

    if (cruiseId && bookingNumber) {
      getPricesCart(dispatch, cruiseId, bookingNumber, false, undefined, {
        handleAvailabilityErrors: false,
      }).catch((error) => {
        console.error('Error refreshing prices after coupon payment failure', error);
      });
    }
  }, [
    paymentCouponErrorCode,
    dispatch,
    setError,
    setValue,
    cruiseId,
    bookingNumber,
    ecouponErrorMessageConfigList,
    ecouponErrorMessageConfigListResources,
  ]);

  const handleCheckout = async (data: { emailOrderConfirmation: string }) => {
    if (isEmailFieldAvailable && isElementVisible('.cmp-sidesummary__email-section')) {
      const isValid = await trigger('emailOrderConfirmation');
      if (!isValid) {
        return;
      }
      dispatch(updateLoggedUserEmailInput(data.emailOrderConfirmation));
    }
    navigateOrRedirectToRoute(checkoutLink || {}, navigate);
  };

  const handleCartSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isEmailFieldAvailable && !isElementVisible('.cmp-sidesummary__email-section')) {
      navigateOrRedirectToRoute(checkoutLink || {}, navigate);
      return;
    }

    handleSubmit(handleCheckout)(event);
  };

  const handleCheckoutEmailChange = useDebounce(() => {
    if (!isCheckoutPage) {
      return;
    }

    trigger('emailOrderConfirmation').then((isValid) => {
      dispatch(updateLoggedUserEmailInput(isValid ? getValues('emailOrderConfirmation') : ''));
    });
  }, 300);

  const handleApplyCoupon = async () => {
    if (appliedECoupon) return;
    const isValid = await trigger('eCouponCode');
    if (!isValid) return;
    const value = eCouponValue?.trim();
    if (!value) return;
    if (cruiseId && bookingNumber) {
      getPricesCart(dispatch, cruiseId, bookingNumber, false, value, {
        handleAvailabilityErrors: false,
      })
        .then((response) => {
          const warningCode = response?._warnings?.[0]?.code;
          if (warningCode) {
            setError('eCouponCode', {
              type: 'manual',
              message: getEcouponErrorMessageByCode(warningCode),
            });
            return;
          }
          const { hasAtLeastOneApplicableItem, allItemsNotApplicable, commonReasonCode } =
            getItemLevelCouponOutcome(response);
          if (hasAtLeastOneApplicableItem) {
            clearErrors('eCouponCode');
            dispatch(setAppliedECoupon(value));
            setValue('eCouponCode', value);
            return;
          }
          if (commonReasonCode) {
            setError('eCouponCode', {
              type: 'manual',
              message: getEcouponErrorMessageByCode(commonReasonCode),
            });
            return;
          }
          if (allItemsNotApplicable) {
            setError('eCouponCode', {
              type: 'manual',
              message: combinedItemErrorsMessage || genericEcouponErrorMessage || '',
            });
            return;
          }
          clearErrors('eCouponCode');
          dispatch(setAppliedECoupon(value));
          setValue('eCouponCode', value);
        })
        .catch((error) => {
          const issueCode = extractEcouponIssueCodeFromError(error);
          if (issueCode) {
            setError('eCouponCode', {
              type: 'manual',
              message: getEcouponErrorMessageByCode(issueCode),
            });
            return;
          }
          console.error('Error applying e-coupon prices update', error);
        });
    }
  };

  const handleRemoveCoupon = () => {
    dispatch(clearAppliedECoupon());
    setValue('eCouponCode', '');
    clearErrors('eCouponCode');
    if (cruiseId && bookingNumber) {
      getPricesCart(dispatch, cruiseId, bookingNumber, false, undefined, {
        handleAvailabilityErrors: false,
      }).catch((error) => {
        console.error('Error removing e-coupon prices update', error);
      });
    }
  };
  return (
    <form
      className={`${styles['cmp-sidesummary']} ${
        pageType === PageType.CHECKOUT_PAGE ? styles['cmp-sidesummary--checkout'] : ''
      }`}
      onSubmit={isCheckoutPage ? (event) => event.preventDefault() : handleCartSubmit}
      noValidate={true}
    >
      <div className={styles['cmp-sidesummary__container']}>
        <section
          className="cmp-sidesummary_section-coupon"
          aria-labelledby="summary-section-coupon"
        >
          {hasECoupon && (
            <>
              <Accordion
                styleOption={styles['cmp-sidesummary__coupon-accordion']}
                question={labels.ecouponText ?? ''}
                answer={''}
                isOpen={isCouponAccordionOpen}
                onToggle={() => setIsCouponAccordionOpen((prev) => !prev)}
                autoClose={true}
                openingIcon={ecouponAccordionOpenIcon || accordionOpenIcon || 'down'}
                closingIcon={ecouponAccordionCloseIcon || accordionCloseIcon || 'up'}
                multi={true}
              >
                <div className={styles['cmp-sidesummary__coupon-content']}>
                  <fieldset className={styles['cmp-sidesummary__coupon-fieldset']}>
                    <legend className="sr-only">
                      {inputFieldLabel || labels.ecouponText || applyCouponCtaLabel || ''}
                    </legend>
                    <div className={styles['cmp-sidesummary__coupon-row']}>
                      <InputText
                        inputType={getKeyboardType(couponInputValidationRules)}
                        styleOptions={styles['cmp-sidesummary__coupon-input']}
                        index={0}
                        name="eCouponCode"
                        label={inputFieldLabel || couponInputPlaceholder || ''}
                        placeholder={couponInputPlaceholder || ''}
                        register={register}
                        errors={errors}
                        clearErrors={clearErrors}
                        disabled={isCouponApplied}
                        handleInputChange={(event: ChangeEvent<HTMLInputElement>) => {
                          if (errors.eCouponCode?.type === 'manual') {
                            clearErrors('eCouponCode');
                          }
                          setValue('eCouponCode', event.target.value, { shouldDirty: true });
                        }}
                        validation={{
                          validate: (value) =>
                            validateFormField(value, couponInputValidationRules || []),
                          onBlur: () => void trigger('eCouponCode'),
                        }}
                      />
                      <Button
                        text={applyCouponCtaLabel ?? ''}
                        styleOption={`cmp-button__primary ${styles['cmp-sidesummary__coupon-cta']}`}
                        buttonType="button"
                        disabled={!isApplyEnabled}
                        onClick={handleApplyCoupon}
                      />
                    </div>
                  </fieldset>
                  {appliedECoupon && (
                    <Chip
                      text={appliedECoupon}
                      icon="close"
                      onClick={handleRemoveCoupon}
                      styleOption={styles['cmp-sidesummary__coupon-chip']}
                    />
                  )}
                </div>
              </Accordion>
              <Divider className={''} />
            </>
          )}
        </section>
        <section
          className="cmp-sidesummary_section-summary"
          aria-labelledby="summary-section-summary-title"
        >
          <Text
            styleOption="header-xs"
            text={labels.summarySectionTitle}
          />
          <div className="priceRow">
            <Text
              styleOption="cmp-text__big"
              text={managePlurals(
                displayItemsCount,
                replacePlaceholders(labels.summarySubTotalText, {
                  count: displayItemsCount.toString(),
                })
              )}
            />
            {isPricesLoading ? (
              <SkeletonLoader
                width="90px"
                height="18px"
                styleOption="cmp-skeleton__loader"
              />
            ) : (
              <Text
                styleOption="price cmp-text__big"
                text={formatCurrency(subtotalPriceAmount)}
              />
            )}
          </div>

          <div className="priceRow">
            {labels.summaryTaxesText && (
              <>
                <Text
                  styleOption="cmp-text__big"
                  text={labels.summaryTaxesText}
                />
                {isPricesLoading ? (
                  <SkeletonLoader
                    width="90px"
                    height="18px"
                    styleOption="cmp-skeleton__loader"
                  />
                ) : (
                  <Text
                    styleOption="price cmp-text__big"
                    text={formatCurrency(taxesAmount)}
                  />
                )}
              </>
            )}
          </div>
          {appliedECoupon && labels.summaryEcouponText && couponAmount > 0 && (
            <div className="priceRow">
              <div>
                <Text
                  styleOption="cmp-text__big"
                  text={labels.summaryEcouponText}
                />
              </div>
              {isPricesLoading ? (
                <SkeletonLoader
                  width="90px"
                  height="18px"
                  styleOption="cmp-skeleton__loader"
                />
              ) : (
                <Text
                  styleOption="price cmp-text__big"
                  text={`-${formatCurrency(couponAmount)}`}
                />
              )}
            </div>
          )}
          <Divider className={''} />
        </section>
        <section
          className="cmp-sidesummary_section-total"
          aria-labelledby="summary-section-total-price"
        >
          <div className="priceRow">
            <Text
              styleOption="cmp-sidesummary_totalPriceText"
              text={labels.summaryTotalText}
            />
            {isPricesLoading ? (
              <SkeletonLoader
                width="110px"
                height="20px"
                styleOption="cmp-skeleton__loader"
              />
            ) : (
              <Text
                styleOption="cmp-sidesummary_totalPriceValue"
                text={formatCurrency(activePrice?.total.afterDiscount)}
              />
            )}
          </div>
          {labels.summarySavedMoneyText && isPricesLoading && (
            <SkeletonLoader
              width="140px"
              height="16px"
              styleOption="cmp-skeleton__loader"
            />
          )}
          {labels.summarySavedMoneyText && hasSavedMoneyAmount && !isPricesLoading && (
            <Text
              styleOption="cmp-sidesummary_savedMoneyText"
              text={replacePlaceholders(labels.summarySavedMoneyText, {
                money: `${formatCurrency(discountAmount || 0)}`,
              })}
            />
          )}
          {showEmailSection && (
            <>
              <Divider className={''} />
              <EmailInputSection
                title={emailOrderConfirmationTitle}
                description={emailOrderConfirmationDescription}
                inputLabel={emailInputLabel}
                inputPlaceholder={emailInputPlaceholder}
                inputRequiredLabel={emailInputRequiredLabel}
                validationRules={
                  isElementVisible('.cmp-sidesummary__email-section')
                    ? emailInputValidationRules
                    : []
                }
                register={
                  register as unknown as UseFormRegister<{ emailOrderConfirmation: string }>
                }
                errors={errors as unknown as FieldErrors<{ emailOrderConfirmation: string }>}
                clearErrors={
                  clearErrors as unknown as UseFormClearErrors<{ emailOrderConfirmation: string }>
                }
                onInputChange={handleCheckoutEmailChange}
                onBlurTrigger={() => {
                  void trigger('emailOrderConfirmation');
                }}
              />
            </>
          )}

          {labels.checkoutButtonText && (
            <Button
              text={'Checkout'}
              styleOption={'cmp-button__primary -full-width'}
              buttonType="submit"
            />
          )}
        </section>
      </div>
    </form>
  );
}

export default SideSummary;
